import React, { useState } from 'react';
import ApiService from '../services/api';

const apiService = ApiService.getInstance();

// ─── Integration Builder UI ─────────────────────────────────────────────
// V1 flow:
//  1. Web: parent pastes a redacted capture log (from the dev tools Network
//     tab → HAR-style export, or from the iOS Cluubz app's "Scan" feature).
//  2. iOS (CluubzCaptureWebView Capacitor plugin): native WKWebView records
//     every URLRequest/response, redacts secrets, returns the log to this
//     screen via the plugin bridge — same code path from there.
//  3. We POST the log to /api/integrations/builder/analyze; Claude returns
//     a config + 0-4 clarifying questions.
//  4. Parent answers the questions, taps Save. We POST to /save which
//     creates a community_integrations row in status='draft'.

declare global {
  interface Window {
    // Capacitor plugin shim. Defined when the app runs inside the iOS
    // Capacitor host; undefined in the browser.
    CluubzCaptureWebView?: {
      open: (opts: { startUrl: string }) => Promise<{ captureLog: any[]; appName?: string }>;
    };
  }
}

export const IntegrationBuilder: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [appName, setAppName] = useState('');
  const [startUrl, setStartUrl] = useState('');
  const [childName, setChildName] = useState('');
  const [clubName, setClubName] = useState('');
  const [captureLogText, setCaptureLogText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [answers, setAnswers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedUuid, setSavedUuid] = useState<string | null>(null);

  const onScan = async () => {
    if (!window.CluubzCaptureWebView) {
      setError('Scan-via-native is only available in the Cluubz iOS app. On the web, paste a redacted capture log below instead.');
      return;
    }
    if (!startUrl) {
      setError('Enter the club app\'s sign-in URL first.');
      return;
    }
    setError(null);
    setScanning(true);
    try {
      const out = await window.CluubzCaptureWebView.open({ startUrl });
      setCaptureLogText(JSON.stringify(out.captureLog, null, 2));
      if (out.appName && !appName) setAppName(out.appName);
    } catch (e: any) {
      setError(`Capture cancelled or failed: ${e?.message || e}`);
    } finally {
      setScanning(false);
    }
  };

  const onAnalyze = async () => {
    setError(null);
    setResult(null);
    let parsed: any[];
    try {
      parsed = JSON.parse(captureLogText);
      if (!Array.isArray(parsed)) throw new Error('not an array');
    } catch (e: any) {
      setError(`Capture log must be valid JSON array — ${e.message}`);
      return;
    }
    setAnalyzing(true);
    try {
      const resp = await apiService.analyzeIntegrationCapture({
        appName: appName || 'Untitled',
        captureLog: parsed,
        userHints: { childName, clubName },
      });
      if (!resp.success) {
        setError(resp.error || 'Analyze failed');
        return;
      }
      const data = resp.data!;
      setResult(data);
      if (data.validationError) {
        setError(`Claude returned an invalid shape: ${data.validationError}. You can still save it as a draft if it looks right.`);
      }
      const qs = (data.config?.clarifyingQuestions || []) as string[];
      setAnswers(qs.map(() => ''));
    } catch (e: any) {
      setError(e?.message || 'Analyze failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const onSave = async () => {
    if (!result?.config) return;
    setSaving(true);
    setError(null);
    try {
      const cfg = {
        ...result.config,
        userAnswers: answers.length ? answers : undefined,
      };
      const resp = await apiService.saveBuiltIntegration(cfg);
      if (!resp.success) {
        setError(resp.error || 'Save failed');
        return;
      }
      setSavedUuid(resp.data!.integrationUuid);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '20px auto', padding: '0 16px', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Build a new integration</h2>
        {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button>}
      </div>

      <p style={{ color: '#555', fontSize: 14 }}>
        Teach Cluubz to read activities from a club app we don't support yet. You log in inside a scan
        window and browse to your child's activities — we look at the traffic and figure out how to
        ask the same questions every night.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
        <label style={labelStyle}>
          App name
          <input value={appName} onChange={e => setAppName(e.target.value)} placeholder="e.g. SwimGo" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Sign-in URL
          <input value={startUrl} onChange={e => setStartUrl(e.target.value)} placeholder="https://app.swimgo.com/login" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Child's name
          <input value={childName} onChange={e => setChildName(e.target.value)} placeholder="Helps us guess which row is them" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Club name
          <input value={clubName} onChange={e => setClubName(e.target.value)} placeholder="e.g. Surrey Sharks" style={inputStyle} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={onScan} disabled={scanning} style={primaryBtn}>
          {scanning ? 'Scanning…' : 'Scan via native window (iOS only)'}
        </button>
      </div>

      <div style={{ marginTop: 18, padding: 14, background: '#fbfbf6', border: '1px dashed #c8c8a8', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Quick capture via Chrome DevTools (web testing)</div>
        <ol style={{ fontSize: 13, color: '#555', margin: '4px 0 4px 18px', padding: 0 }}>
          <li>Open the club app in Chrome → press <kbd>F12</kbd> → <strong>Network</strong> tab</li>
          <li>Tick <strong>Preserve log</strong>; log in and browse to your child's activities</li>
          <li>Right-click anywhere in the requests list → <strong>Save all as HAR with content</strong></li>
          <li>Drop the .har file below — we'll redact secrets and load the capture for you</li>
        </ol>
        <input
          type="file"
          accept=".har,application/json,application/octet-stream"
          onChange={async e => {
            const file = e.target.files?.[0];
            if (!file) return;
            setError(null);
            try {
              const text = await file.text();
              const har = JSON.parse(text);
              const log = harToCaptureLog(har);
              if (log.length === 0) {
                setError('HAR loaded but contained no requests we could use.');
                return;
              }
              setCaptureLogText(JSON.stringify(log, null, 2));
              if (!appName && har?.log?.pages?.[0]?.title) {
                try { setAppName(new URL(har.log.pages[0].title).hostname); } catch { /* */ }
              }
            } catch (err: any) {
              setError(`Could not parse HAR file: ${err?.message || err}`);
            }
          }}
          style={{ marginTop: 4 }}
        />
      </div>

      <label style={{ ...labelStyle, marginTop: 14 }}>
        Capture log (redacted JSON array)
        <textarea
          value={captureLogText}
          onChange={e => setCaptureLogText(e.target.value)}
          rows={10}
          placeholder='Drop a HAR above, or paste a redacted JSON array of {request, response} entries.'
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, minHeight: 160 }}
        />
        {captureLogText && (() => {
          try {
            const parsed = JSON.parse(captureLogText);
            return Array.isArray(parsed)
              ? <span style={{ fontSize: 12, color: '#1f6b22', marginTop: 4 }}>✓ {parsed.length} entries loaded</span>
              : null;
          } catch { return <span style={{ fontSize: 12, color: '#7a1f1f', marginTop: 4 }}>Not valid JSON yet</span>; }
        })()}
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={onAnalyze} disabled={analyzing || !captureLogText} style={primaryBtn}>
          {analyzing ? 'Analyzing…' : 'Analyze with Claude'}
        </button>
      </div>

      {error && <div style={errBox}>{error}</div>}

      {result?.config && (
        <div style={{ marginTop: 20, padding: 16, background: '#f7faf7', border: '1px solid #d6e3d6', borderRadius: 8 }}>
          <h3 style={{ marginTop: 0 }}>Proposed integration</h3>
          <div style={{ fontSize: 14 }}>
            <strong>Auth pattern:</strong> {result.config.auth?.pattern}
            {result.config.auth?.additionalNotes && <div style={{ color: '#555', marginTop: 4 }}>{result.config.auth.additionalNotes}</div>}
          </div>
          <div style={{ marginTop: 12, fontSize: 14 }}>
            <strong>Endpoints found:</strong>
            <ul style={{ margin: '6px 0' }}>
              {(result.config.endpoints || []).map((ep: any, i: number) => (
                <li key={i}><code>{ep.method} {ep.urlTemplate}</code> — {ep.purpose}</li>
              ))}
            </ul>
          </div>
          <div style={{ marginTop: 12, fontSize: 14 }}>
            <strong>Field mapping confidence:</strong>
            <ul style={{ margin: '6px 0' }}>
              {(result.config.fieldMapping || []).map((m: any, i: number) => (
                <li key={i}><code>{m.cluubzField}</code> ← <code>{m.source}</code> <span style={{ color: confidenceColor(m.confidence) }}>({m.confidence})</span></li>
              ))}
            </ul>
          </div>

          {(result.config.blockers || []).length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: '#fff5e0', border: '1px solid #f1c97a', borderRadius: 6 }}>
              <strong>Blockers</strong>
              <ul style={{ margin: '6px 0' }}>
                {result.config.blockers.map((b: any, i: number) => (
                  <li key={i}><strong>{b.type}:</strong> {b.explanation}</li>
                ))}
              </ul>
            </div>
          )}

          {(result.config.clarifyingQuestions || []).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong>Quick questions before we save:</strong>
              {result.config.clarifyingQuestions.map((q: string, i: number) => (
                <div key={i} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 14 }}>{q}</div>
                  <input
                    value={answers[i] || ''}
                    onChange={e => {
                      const next = [...answers];
                      next[i] = e.target.value;
                      setAnswers(next);
                    }}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={onSave} disabled={saving || !!savedUuid} style={primaryBtn}>
              {saving ? 'Saving…' : savedUuid ? 'Saved as draft' : 'Save as draft'}
            </button>
          </div>
          {savedUuid && (
            <ConnectAndTest integrationUuid={savedUuid} appName={result.config.appName} staticHeaders={result.config.auth?.staticHeaders || []} />
          )}
        </div>
      )}
    </div>
  );
};

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 13, color: '#333', gap: 4 };
const inputStyle: React.CSSProperties = { padding: '8px 10px', border: '1px solid #c5c5c5', borderRadius: 4, fontSize: 14 };
const primaryBtn: React.CSSProperties = { padding: '8px 16px', background: '#177', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' };
const errBox: React.CSSProperties = { marginTop: 12, padding: 10, background: '#fde8e8', border: '1px solid #f5b5b5', borderRadius: 6, color: '#7a1f1f', fontSize: 13 };

function confidenceColor(c: string) {
  if (c === 'high') return '#1f6b22';
  if (c === 'medium') return '#8a6a00';
  return '#7a1f1f';
}

// ─── Connect & Test sub-component ───────────────────────────────────────
// Shown after the parent saves the inferred config. Collects the parent's
// credentials (encrypted server-side) and runs the connector once to prove
// it works before the nightly cron picks it up.
type StaticHeaderDecl = { name: string; scope?: 'shared_app_secret' | 'user_supplied'; configHint?: string };

const ConnectAndTest: React.FC<{ integrationUuid: string; appName: string; staticHeaders: StaticHeaderDecl[] }> = ({ integrationUuid, appName, staticHeaders }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ activitiesWritten: number; clubs: number } | null>(null);
  const [connected, setConnected] = useState(false);

  const onConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const secrets: any = {};
      if (username) secrets.username = username;
      if (password) secrets.password = password;
      if (apiKey) secrets.apiKey = apiKey;
      const filledHeaders = Object.fromEntries(Object.entries(headerValues).filter(([, v]) => v));
      if (Object.keys(filledHeaders).length) secrets.staticHeaders = filledHeaders;
      const resp = await apiService.connectCommunityIntegration(integrationUuid, secrets);
      if (!resp.success) { setError(resp.error || 'Connect failed'); return; }
      setConnected(true);
    } catch (e: any) {
      setError(e?.message || 'Connect failed');
    } finally {
      setConnecting(false);
    }
  };

  const onTest = async () => {
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await apiService.testRunCommunityIntegration(integrationUuid);
      if (!resp.success) { setError(resp.error || 'Test run failed'); return; }
      setTestResult(resp.data!);
    } catch (e: any) {
      setError(e?.message || 'Test run failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ marginTop: 16, padding: 14, background: '#fff', border: '1px solid #d8d8d8', borderRadius: 8 }}>
      <h4 style={{ marginTop: 0 }}>Connect &amp; test {appName}</h4>
      <p style={{ fontSize: 13, color: '#555' }}>
        Enter the credentials you used in the scan so Cluubz can replay the connector tonight.
        Test it once to make sure activities arrive correctly.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={smallLabel}>Username / email
          <input value={username} onChange={e => setUsername(e.target.value)} style={inputStyle2} autoComplete="username" />
        </label>
        <label style={smallLabel}>Password
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle2} autoComplete="current-password" />
        </label>
        <label style={{ ...smallLabel, gridColumn: '1 / -1' }}>API key (only for API-key apps)
          <input value={apiKey} onChange={e => setApiKey(e.target.value)} style={inputStyle2} />
        </label>
        {staticHeaders.map(h => (
          <label key={h.name} style={{ ...smallLabel, gridColumn: '1 / -1' }}>
            {h.name} {h.scope === 'shared_app_secret' ? '(shared app secret — paste from a real client request)' : '(per-user value)'}
            {h.configHint && <span style={{ display: 'block', color: '#666', fontSize: 11, fontWeight: 'normal', marginTop: 2 }}>{h.configHint}</span>}
            <input
              value={headerValues[h.name] || ''}
              onChange={e => setHeaderValues(s => ({ ...s, [h.name]: e.target.value }))}
              style={inputStyle2}
              placeholder={`Value for ${h.name}`}
            />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={onConnect} disabled={connecting || connected} style={primaryBtn2}>
          {connecting ? 'Saving…' : connected ? 'Credentials saved ✓' : 'Save credentials'}
        </button>
        <button onClick={onTest} disabled={!connected || testing} style={primaryBtn2}>
          {testing ? 'Running…' : 'Run sync once'}
        </button>
      </div>
      {testResult && (
        <div style={{ marginTop: 10, color: '#1f6b22', fontSize: 13 }}>
          Wrote {testResult.activitiesWritten} activity sessions across {testResult.clubs} child group{testResult.clubs === 1 ? '' : 's'}. They'll show up on the home screen + term planner.
        </div>
      )}
      {error && <div style={{ marginTop: 10, color: '#7a1f1f', fontSize: 13 }}>{error}</div>}
    </div>
  );
};

// ─── HAR → captureLog converter (browser-side redaction) ─────────────────
// Mirrors the redaction rules in CluubzCaptureWebViewPlugin.swift so the
// same prompt + validator works for both desktop-DevTools captures and
// future iOS native captures.

const SENSITIVE_BODY_KEYS_WEB = [
  'password', 'pass', 'pwd', 'passwd', 'secret', 'token',
  'refresh_token', 'refreshToken', 'access_token', 'accessToken',
  'id_token', 'client_secret',
];
const SENSITIVE_HEADER_NAMES_WEB = [
  'authorization', 'cookie', 'set-cookie', 'x-api-key', 'apikey',
  'x-auth-token', 'proxy-authorization',
];

function shapeOfWeb(s: string): string {
  const len = (s || '').length;
  const lower = (s || '').toLowerCase();
  if (lower.startsWith('bearer ')) return `[REDACTED:bearer:${len}chars]`;
  if (lower.startsWith('basic '))  return `[REDACTED:basic:${len}chars]`;
  return `[REDACTED:${len}chars]`;
}

function redactBodyWeb(body: string): string {
  if (!body) return '';
  // Form-encoded redaction
  if (body.includes('=') && body.includes('&') && !body.trim().startsWith('{')) {
    const pairs = body.split('&').map(p => {
      const idx = p.indexOf('=');
      if (idx < 0) return p;
      const k = p.slice(0, idx);
      if (SENSITIVE_BODY_KEYS_WEB.includes(k.toLowerCase())) {
        return `${k}=[REDACTED:${k.toLowerCase()}]`;
      }
      return p;
    });
    return pairs.join('&');
  }
  // JSON redaction — naive regex pass; matches the Swift version.
  let out = body;
  for (const key of SENSITIVE_BODY_KEYS_WEB) {
    out = out.replace(new RegExp(`"${key}"\\s*:\\s*"[^"]*"`, 'gi'),
      `"${key}":"[REDACTED:${key.toLowerCase()}]"`);
    out = out.replace(new RegExp(`"${key}"\\s*:\\s*[0-9]+`, 'gi'),
      `"${key}":"[REDACTED:${key.toLowerCase()}]"`);
  }
  return out;
}

function redactHeadersWeb(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(headers)) {
    if (SENSITIVE_HEADER_NAMES_WEB.includes(k.toLowerCase())) {
      out[k] = shapeOfWeb(headers[k]);
    } else {
      out[k] = headers[k];
    }
  }
  return out;
}

interface HarEntry {
  request?: { method?: string; url?: string; headers?: Array<{ name: string; value: string }>; postData?: { text?: string; mimeType?: string } };
  response?: { status?: number; headers?: Array<{ name: string; value: string }>; content?: { text?: string; mimeType?: string }; bodySize?: number };
  startedDateTime?: string;
}

function headersArrayToObject(arr?: Array<{ name: string; value: string }>): Record<string, string> {
  const o: Record<string, string> = {};
  if (!arr) return o;
  for (const h of arr) o[h.name] = h.value;
  return o;
}

// Hosts/services that are universally noise in a "log into App X and browse to
// my activities" capture. None of these ever inform the connector config.
const NOISE_HOSTS_WEB = /(google-analytics|googletagmanager|doubleclick|googleadservices|gstatic|googleapis\.com\/(fonts|maps|ads)|fonts\.gstatic|hotjar|datadoghq|sentry\.io|segment\.io|mixpanel|amplitude|cloudflareinsights|cloudflare\.com\/cdn-cgi|hubspot|hs-analytics|facebook\.net|connect\.facebook|fbcdn|cdn\.cookielaw|onetrust|braze|bing\.com\/bat|adsrvr|adsystem|criteo|taboola|outbrain|recaptcha|stripe\.com|stripe\.network)/i;
const NOISE_PATHS_WEB = /(\/(collect|beacon|ping|track|pixel|tr\?|__ptq\.gif|__ptc\.gif|ccm\/collect)|\.gif(\?|$))/i;

// Heuristic relevance score — higher = more likely the connector author needs
// to see this entry. We trim to TOP_N entries after scoring.
function relevanceScoreWeb(e: HarEntry, primaryHost: string | null): number {
  const url = (e.request?.url || '').toLowerCase();
  const method = (e.request?.method || 'GET').toUpperCase();
  const respHeaders = headersArrayToObject(e.response?.headers);
  const respMime = e.response?.content?.mimeType || '';
  let score = 0;
  if (/(login|signin|authenticate|auth\/|oauth|\/token|sessions?|jwt)/i.test(url)) score += 100;
  if (method === 'POST') score += 30;
  // Set-Cookie almost always matters for auth inference.
  if (Object.keys(respHeaders).some(h => h.toLowerCase() === 'set-cookie')) score += 60;
  // Bearer/api-key headers in REQUESTS pin authenticated calls.
  const reqHeaders = headersArrayToObject(e.request?.headers);
  if (Object.keys(reqHeaders).some(h => /authorization|x-api-key|apikey|x-auth-token/i.test(h))) score += 40;
  // JSON responses on the primary host are the data plane.
  if (/json/i.test(respMime)) score += 25;
  if (primaryHost && url.includes(primaryHost)) score += 20;
  // HTML pages can be the bridge: post-login bootstrap that embeds the JWT
  // in an inline <script>. If the body holds anything JWT-shaped, score it
  // up; otherwise treat HTML as low-signal noise.
  const respBody = e.response?.content?.text || '';
  const looksLikeInlineToken = /["'][A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}["']/.test(respBody)
    || /(jwt|access_?token|bearer)\s*[:=]\s*["'][A-Za-z0-9_.\-]{40,}/i.test(respBody);
  if (/text\/html/i.test(respMime)) {
    if (looksLikeInlineToken) score += 70;
    else if (!/login|auth/i.test(url)) score -= 10;
  }
  // Penalize giant marketing pages, asset proxies.
  if ((e.response?.bodySize || 0) > 200_000) score -= 20;
  return score;
}

function harToCaptureLog(har: any): any[] {
  const entries: HarEntry[] = har?.log?.entries || [];
  // Detect the "primary host" — the eTLD+1 the parent is logging into. The
  // first login-ish POST is the strongest signal; fall back to the most
  // frequent host in the capture.
  const hostFreq = new Map<string, number>();
  let primaryHost: string | null = null;
  for (const e of entries) {
    const url = e.request?.url || '';
    let h: string;
    try { h = new URL(url).hostname.split('.').slice(-2).join('.'); } catch { continue; }
    hostFreq.set(h, (hostFreq.get(h) || 0) + 1);
    if (!primaryHost && (e.request?.method || '').toUpperCase() === 'POST' &&
        /(login|authenticate|signin|auth\/|oauth|\/token|sessions?|jwt)/i.test(url)) {
      primaryHost = h;
    }
  }
  if (!primaryHost) {
    primaryHost = Array.from(hostFreq).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  }

  const scored: { entry: HarEntry; score: number }[] = [];
  for (const e of entries) {
    const url = e.request?.url || '';
    if (/\.(png|jpe?g|gif|svg|webp|woff2?|ttf|eot|ico|css|map|mp4|webm)(\?|$)/i.test(url)) continue;
    if (NOISE_HOSTS_WEB.test(url) || NOISE_PATHS_WEB.test(url)) continue;
    const respMime = e.response?.content?.mimeType || '';
    if (respMime && !/(json|xml|text|html|javascript|x-www-form-urlencoded)/i.test(respMime)) continue;
    // Drop static .js bundles — they're huge and rarely carry config Claude
    // can use. JSON/HTML stays.
    if (/javascript/i.test(respMime) && /\.js(\?|$)/i.test(url)) continue;
    const score = relevanceScoreWeb(e, primaryHost);
    if (score < -20) continue; // hard floor for obviously irrelevant noise
    scored.push({ entry: e, score });
  }

  // Sort by score desc, take top 40 (the model rarely needs more), then
  // restore chronological order so Claude reads the flow forwards.
  scored.sort((a, b) => b.score - a.score);
  const top = scored.slice(0, 40);
  top.sort((a, b) => {
    const ta = a.entry.startedDateTime ? new Date(a.entry.startedDateTime).getTime() : 0;
    const tb = b.entry.startedDateTime ? new Date(b.entry.startedDateTime).getTime() : 0;
    return ta - tb;
  });

  const out: any[] = [];
  for (const { entry: e } of top) {
    const reqHeaders = redactHeadersWeb(headersArrayToObject(e.request?.headers));
    const respHeaders = redactHeadersWeb(headersArrayToObject(e.response?.headers));
    const reqBody = redactBodyWeb(e.request?.postData?.text || '');
    // Send up to 200KB of each text response — the backend's Haiku
    // preprocessor condenses bodies that big into a structured bodyCondensed
    // field before the main Sonnet analyser sees them. The cap must be tall
    // enough to include bootstrap tokens that SPAs sometimes emit near the
    // bottom of long server-rendered pages (Pitchero's _ph.jwt sits at offset
    // ~70KB of a 74KB membership HTML page).
    const respBody = redactBodyWeb((e.response?.content?.text || '').slice(0, 200000));
    out.push({
      request:  { method: e.request?.method || 'GET', url: e.request?.url || '', headers: reqHeaders, body: reqBody },
      response: { status: e.response?.status || 0, headers: respHeaders, body: respBody },
      ts: e.startedDateTime ? Math.floor(new Date(e.startedDateTime).getTime() / 1000) : Math.floor(Date.now() / 1000),
    });
  }
  return out;
}

const smallLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#444', gap: 3 };
const inputStyle2: React.CSSProperties = { padding: '6px 8px', border: '1px solid #c5c5c5', borderRadius: 4, fontSize: 13 };
const primaryBtn2: React.CSSProperties = { padding: '6px 12px', background: '#177', color: '#fff', border: 'none', borderRadius: 5, fontSize: 13, cursor: 'pointer' };

export default IntegrationBuilder;
